// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { split } from "@transformers/schema";
import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreator from "@modules/service/actionCreator";
import * as selectors from "@modules/service/selectors";
import { Button, Col, Row } from "@components";
import { type StepProps, View } from "@components";
import type { ServiceKpis } from "@modules/service/types";
import type { Rtw, RtwLateSubmissionReason } from "@modules/service/reducer/rtw/types";
import { RtwCompliance, RtwReasonForLateSubmission } from "@modules/service/components";
import { CommonComplianceKpisForm } from "@modules/service/components/common/common-compliance-kpis";

type StepData = $Exact<{
  compliance: Object,
  kpis: Array<ServiceKpis>,
  lateSubmissionReasons: Array<RtwLateSubmissionReason> | null,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Rtw,
}>;

type PropsFromDispatch = $Exact<{
  setRtwDraft: typeof actionCreator.SetRtwDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepContent extends React.Component<Props> {
  form: *;
  validateCompliance: *;
  validateKpis: *;
  validateLateSubmissionReasons: *;

  componentDidMount() {
    if (this.props.schema) {
      this.setValidators();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.setValidators();
    }
  }

  setValidators = () => {
    this.validateCompliance = new Ajv().compile(this.schema.compliance);
    this.validateKpis = new Ajv().compile(this.schema.kpis);
    this.validateLateSubmissionReasons = new Ajv().compile(this.schema.lateSubmissionReasons);
  };

  get data() {
    const draft = this.props.draft?.content || {};
    return {
      compliance: {
        ...(draft.expiryPeriodInSeconds !== undefined
          ? { expiryPeriodInSeconds: draft.expiryPeriodInSeconds }
          : {}),
        ...(draft.reminderTimeInSeconds !== undefined
          ? { reminderTimeInSeconds: draft.reminderTimeInSeconds }
          : {}),
        ...(draft.copyInManagersManager !== undefined
          ? { copyInManagersManager: draft.copyInManagersManager }
          : {}),
        ...(draft.lateReasonCapture !== undefined
          ? { lateReasonCapture: draft.lateReasonCapture }
          : {}),
        ...(draft.reminderType !== undefined ? { reminderType: draft.reminderType } : {}),
      },
      kpis: draft.kpis || [],
      lateSubmissionReasons: draft.lateSubmissionReasons || [],
    };
  }

  // The following schema manipulating is required until the backend are
  // able to control their domain
  get schema() {
    const lateSubmissionReasonsSchema = ((this.props.schema || {}).properties || {})
      .lateSubmissionReasons;
    const kpisSchema = ((this.props.schema || {}).properties || {}).kpis;

    return {
      compliance: split(this.props.schema, [
        "expiryPeriodInSeconds",
        "reminderTimeInSeconds",
        "copyInManagersManager",
        "lateReasonCapture",
        "reminderType",
      ]),
      kpis: kpisSchema
        ? {
            ...kpisSchema,
            minItems: 1,
          }
        : {},
      lateSubmissionReasons: lateSubmissionReasonsSchema
        ? {
            ...lateSubmissionReasonsSchema,
            items: {
              ...lateSubmissionReasonsSchema.items,
              properties: {
                ...lateSubmissionReasonsSchema.items.properties,
                reasons: {
                  ...lateSubmissionReasonsSchema.items.properties.reasons,
                  minLength: 1,
                },
              },
            },
            minItems: 1,
            uniqueItems: true,
          }
        : {},
    };
  }

  isValid = (data: StepData) => {
    if (!this.validateCompliance || !this.validateKpis || !this.validateLateSubmissionReasons) {
      return false;
    }

    const cleanCompliance = removeNullAndUndefinedProps(data.compliance);
    const cleanKpis = (data.kpis || []).map(d => removeNullAndUndefinedProps(d));
    const cleanLateSubmissionReasons = (data.lateSubmissionReasons || []).map(d =>
      removeNullAndUndefinedProps(d),
    );

    const isValid =
      this.validateCompliance(cleanCompliance) &&
      this.validateKpis(cleanKpis) &&
      (!cleanCompliance.lateReasonCapture ||
        this.validateLateSubmissionReasons(cleanLateSubmissionReasons));

    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn(
        "isValid errors",
        this.validateCompliance.errors,
        this.validateKpis.errors,
        this.validateLateSubmissionReasons.errors,
      );
    }
    return isValid;
  };

  handleFormRef = (ref: *) => {
    this.form = ref;
  };

  submitForm = () => {
    this.submitCompliance({ formData: (this.data || {}).compliance });
  };

  submitCompliance = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit({}, true);
    }
  };

  updateCompliance = (formData: Object) => {
    const content = this.props.draft.content;

    if (formData.lateReasonCapture === false) content.lateSubmissionReasons = null;
    this.props.setRtwDraft({
      ...this.props.draft,
      content: {
        ...content,
        ...formData,
      },
    });
  };

  updateKpis = (data: Array<ServiceKpis>) => {
    this.props.setRtwDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        kpis: data,
      },
    });
  };

  updateLateReasons = (data: Array<*>) => {
    this.props.setRtwDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        lateSubmissionReasons: data,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <View gap={6}>
        {this.props.data && (
          <RtwCompliance
            onRef={this.handleFormRef}
            data={this.data.compliance}
            schema={this.schema.compliance}
            onChange={this.updateCompliance}
            onSubmit={this.submitCompliance}
          />
        )}
        <View className="gs-container" gap={3}>
          <h6>{this.props.t("RETURN_TO_WORK_DETAILS_COMPLIANCE")}</h6>
          {this.props.data && (
            <CommonComplianceKpisForm
              data={this.data.kpis}
              schema={this.schema.kpis.items}
              onChange={this.updateKpis}
            />
          )}
        </View>
        {this.data.compliance.lateReasonCapture && (
          <View className="gs-container">
            {this.data && (
              <RtwReasonForLateSubmission
                data={this.data.lateSubmissionReasons}
                schema={this.schema.lateSubmissionReasons.items}
                onChange={this.updateLateReasons}
              />
            )}
          </View>
        )}
        {!this.props.editing && (
          <View justifyContent="end" direction="row">
            <Button onClick={this.submitForm} disabled={!this.isValid(this.data)}>
              {this.props.t("RETURN_TO_WORK_STEP2_NEXT_BUTTON")}
            </Button>
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: selectors.getRtwSchema(state),
    draft: selectors.getRtwDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setRtwDraft: actionCreator.SetRtwDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepContent);
